import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAssistantContext } from '../utils/assistantContext';
import { useNavigate } from 'react-router-dom';
import TagFilterBar from './TagFilterBar';
import RecruiterTagsPage from './RecruiterTagsPage';
import BulkActionBar from './shell/BulkActionBar';
import FilterSelect from './shell/FilterSelect';
import CandidatePreviewPanel from './CandidatePreviewPanel';
import { exportCandidatesToCSV } from '../utils/exportUtils';

/**
 * CandidateTable — Shared table + filtering component for candidate archives.
 * Used by CandidateArchive (full page) and SearchDetailPage (embedded tab).
 *
 * Props:
 *   candidates, loading, onRefresh, filters, setFilters,
 *   filterOptions, pipelines, savedViews, onSaveView, onDeleteView, onReorderViews,
 *   onBulkAddToPipeline, onBulkAddTag, onBulkDelete, onDeleteCandidate,
 *   onCandidateClick, mode, embedded, showPipelineFilter, showQuickFilters,
 *   showTagFilter, showSavedViews, toolbarExtra, showAddButton, onAddButtonClick,
 *   activeFilterCount, clearFilters
 */

const CandidateTable = ({
  candidates = [],
  loading = false,
  onRefresh,
  initialFilters,
  onFiltersChange,
  filterOptions = { referral_sources: [], tags: [], locations: [] },
  pipelines = [],
  savedViews = [],
  onSaveView,
  onDeleteView,
  onReorderViews,
  onBulkAddToPipeline,
  onBulkAddTag,
  onBulkDelete,
  onBulkSyncLinkedIn,
  onSelectionChange,
  clearSelectionTrigger,
  syncingIds,
  onDeleteCandidate,
  onCandidateClick,
  mode = 'recruiter',
  embedded = false,
  showPipelineFilter = true,
  showQuickFilters = true,
  showTagFilter = true,
  showSavedViews = true,
  showAddButton = true,
  onAddButtonClick,
  toolbarExtra,
  suppressBulkBar = false,
  rowActions,
  rowAfterName,
  contextPipelineId,
  exportLabel,
}) => {
  const navigate = useNavigate();
  const isClient = mode === 'client';

  // Preview panel state
  const [previewCandidateId, setPreviewCandidateId] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [showViewsSidebar, setShowViewsSidebar] = useState(false);
  const [showSaveViewModal, setShowSaveViewModal] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [showQuickPanel, setShowQuickPanel] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);

  // Selection state
  const [selectedCandidates, setSelectedCandidatesRaw] = useState(new Set());
  const setSelectedCandidates = (newSet) => {
    setSelectedCandidatesRaw(newSet);
    if (onSelectionChange) onSelectionChange(Array.from(newSet));
  };
  // Allow parent to clear selection
  useEffect(() => {
    if (clearSelectionTrigger) { setSelectedCandidatesRaw(new Set()); if (onSelectionChange) onSelectionChange([]); }
  }, [clearSelectionTrigger]);
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [bulkAction, setBulkAction] = useState(''); // 'add_to_pipeline', 'delete', 'add_tag'
  const [showPipelineDropdown, setShowPipelineDropdown] = useState(false);
  const [pipelineSearchQuery, setPipelineSearchQuery] = useState('');
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  
  // Delete confirmation modal state
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'sent_at', direction: 'desc' });

  // Tag filter state (advanced AND/OR/NOT logic)
  const [tagFilter, setTagFilter] = useState({ groups: [], groupOperator: 'OR' });
  const [tagsView, setTagsView] = useState(false); // recruiter tag-manager view toggle
  const [tagSelection, setTagSelection] = useState(null); // {count,onExport,onClear} from RecruiterTagsPage
  const [pendingLinkTag, setPendingLinkTag] = useState(null); // tag awaiting merge-choice when a filter already exists

  // Filters state
  const defaultFilters = {
    search: '',
    pipeline_id: '',
    status: '',
    interview_status: '',
    location: '',
    us_only: '',
    referral_source: '',
    spoken_to: '',
    open_to_remote: '',
    remote_only: '',
    open_to_relocation: '',
    do_not_contact: '',
    is_clean: '',
    tags: '',
    min_expected_base: '',
    min_expected_total: '',
    min_current_base: '',
    max_current_base: '',
    min_current_total: '',
    max_current_total: '',
    max_obligation_days: ''
  };
  const [filters, setFilters] = useState({ ...defaultFilters, ...initialFilters });

  // Notify parent when filters change (debounced)
  useEffect(() => {
    if (onFiltersChange) {
      const timer = setTimeout(() => onFiltersChange(filters), 300);
      return () => clearTimeout(timer);
    }
  }, [filters]);

  const activeFilterCount = useMemo(() => {
    return Object.values(filters).filter(v => v !== '' && v !== null && v !== undefined).length;
  }, [filters]);

  // Count active quick filters specifically
  const quickFilterCount = useMemo(() => {
    let count = 0;
    if (filters.location) count++;
    if (filters.referral_source) count++;
    if (filters.status) count++;
    if (filters.interview_status) count++;
    if (filters.open_to_remote) count++;
    if (filters.remote_only) count++;
    if (filters.open_to_relocation) count++;
    return count;
  }, [filters]);

  const clearFilters = () => setFilters({ ...defaultFilters });

  // Default column order (user can reorder)
  const defaultColumnOrder = [
    'name', 'title', 'company', 'tags', 'location', 'email', 'phone',
    'pipeline', 'stage', 'placed', 'interviewing', 'referral_source',
    'current_base', 'current_total', 'expected_base',
    'remote', 'relocation',
    'date_updated', 'notes_updated', 'sent_date', 'rating'
  ];

  // Column order state
  const [columnOrder, setColumnOrder] = useState(defaultColumnOrder);
  const [draggedColumn, setDraggedColumn] = useState(null);

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    title: true,
    company: true,
    tags: true,
    location: true,
    email: true,
    phone: true,
    pipeline: true,
    stage: false,
    placed: false,
    interviewing: false,
    referral_source: false,
    current_base: false,
    current_total: false,
    expected_base: false,
    remote: true,
    relocation: false,
    date_updated: true,
    notes_updated: false,
    sent_date: true,
    rating: false
  });

  // Column widths state for resizing
  const [columnWidths, setColumnWidths] = useState({});
  const [resizing, setResizing] = useState(null);
  const tableRef = useRef(null);

  // Column definitions
  const columnDefs = {
    name: { label: 'Name', defaultWidth: 200, minWidth: 150, alwaysVisible: true, sortKey: 'name' },
    title: { label: 'Title', defaultWidth: 150, minWidth: 100, sortKey: null },
    company: { label: 'Company', defaultWidth: 140, minWidth: 100, sortKey: 'client_current_company' },
    tags: { label: 'Tags', defaultWidth: 140, minWidth: 80, sortKey: null },
    location: { label: 'Location', defaultWidth: 120, minWidth: 80, sortKey: 'display_location' },
    email: { label: 'Email', defaultWidth: 180, minWidth: 120, sortKey: 'email' },
    phone: { label: 'Phone', defaultWidth: 130, minWidth: 100, sortKey: null },
    pipeline: { label: 'Search', defaultWidth: 140, minWidth: 100, sortKey: null },
    stage: { label: 'Interview Stage', defaultWidth: 120, minWidth: 80, sortKey: null },
    placed: { label: 'Placed', defaultWidth: 70, minWidth: 50, sortKey: 'has_placed' },
    interviewing: { label: 'Interviewing', defaultWidth: 90, minWidth: 70, sortKey: 'has_interviewing' },
    referral_source: { label: 'Referral Source', defaultWidth: 130, minWidth: 80, sortKey: 'referral_source' },
    current_base: { label: 'Base Salary', defaultWidth: 100, minWidth: 80, sortKey: 'current_base_salary' },
    current_total: { label: 'Total Comp', defaultWidth: 100, minWidth: 80, sortKey: 'current_total_comp' },
    expected_base: { label: 'Expected', defaultWidth: 110, minWidth: 80, sortKey: 'expected_base_min' },
    remote: { label: 'Remote', defaultWidth: 70, minWidth: 50, sortKey: 'open_to_remote' },
    relocation: { label: 'Reloc.', defaultWidth: 60, minWidth: 50, sortKey: 'open_to_relocation' },
    date_updated: { label: 'Updated', defaultWidth: 85, minWidth: 70, sortKey: 'updated_at' },
    notes_updated: { label: 'Last Note', defaultWidth: 85, minWidth: 70, sortKey: 'last_note_at' },
    sent_date: { label: 'Added', defaultWidth: 85, minWidth: 70, sortKey: 'sent_at' },
    rating: { label: 'Rating', defaultWidth: 70, minWidth: 50, sortKey: 'rating' }
  };

  // Build allColumns array from order
  const allColumns = columnOrder.map(key => ({ key, ...columnDefs[key] }));

  // Get column width
  const getColumnWidth = (key) => columnWidths[key] || columnDefs[key]?.defaultWidth || 100;

  // Column drag and drop handlers
  const handleColumnDragStart = (e, columnKey) => {
    setDraggedColumn(columnKey);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', columnKey);
    // Add visual feedback
    e.target.style.opacity = '0.5';
  };

  const handleColumnDragEnd = (e) => {
    setDraggedColumn(null);
    e.target.style.opacity = '1';
  };

  const handleColumnDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleColumnDrop = (e, targetColumnKey) => {
    e.preventDefault();
    if (!draggedColumn || draggedColumn === targetColumnKey) return;
    
    const newOrder = [...columnOrder];
    const draggedIndex = newOrder.indexOf(draggedColumn);
    const targetIndex = newOrder.indexOf(targetColumnKey);
    
    // Remove dragged column and insert at target position
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedColumn);
    
    setColumnOrder(newOrder);
    setDraggedColumn(null);
  };

  // Handle column resize
  const handleResizeStart = (e, columnKey) => {
    e.preventDefault();
    e.stopPropagation();
    setResizing({ key: columnKey, startX: e.clientX, startWidth: getColumnWidth(columnKey) });
  };

  useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e) => {
      const diff = e.clientX - resizing.startX;
      const col = columnDefs[resizing.key];
      const newWidth = Math.max(col?.minWidth || 50, resizing.startWidth + diff);
      setColumnWidths(prev => ({ ...prev, [resizing.key]: newWidth }));
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing]);

  // Sorting function
  const handleSort = (sortKey) => {
    if (!sortKey) return;
    setSortConfig(prev => ({
      key: sortKey,
      direction: prev.key === sortKey && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Filter and sort candidates
  const sortedCandidates = useMemo(() => {
    // First apply tag filter
    let filtered = candidates;
    
    // Apply tag filter if there are groups with conditions
    if (tagFilter.groups && tagFilter.groups.length > 0) {
      const hasConditions = tagFilter.groups.some(g => g.conditions && g.conditions.length > 0);
      
      if (hasConditions) {
        // Debug: Log tag filter state
        console.log('=== TAG FILTER DEBUG ===');
        console.log('Tag Filter Groups:', JSON.stringify(tagFilter.groups, null, 2));
        console.log('Total candidates before filter:', candidates.length);
        
        // Debug: Log first few candidates' custom_tags
        candidates.slice(0, 3).forEach((c, i) => {
          console.log(`Candidate ${i} (${c.name}):`, {
            custom_tags: c.custom_tags,
            custom_tags_keys: c.custom_tags ? Object.keys(c.custom_tags) : []
          });
        });
        
        filtered = candidates.filter(candidate => {
          // Get candidate's tag IDs as strings for consistent comparison
          const candidateTagIds = candidate.custom_tags ? Object.keys(candidate.custom_tags) : [];
          
          // Evaluate each group
          const groupResults = tagFilter.groups.map(group => {
            if (!group.conditions || group.conditions.length === 0) return true;
            
            // Evaluate conditions within group based on group operator
            const conditionResults = group.conditions.map(condition => {
              // Compare as strings to ensure consistency
              const tagIdStr = String(condition.tagId);
              const hasTag = candidateTagIds.includes(tagIdStr);
              return condition.not ? !hasTag : hasTag;
            });
            
            // Combine conditions based on group operator
            if (group.operator === 'AND') {
              return conditionResults.every(r => r);
            } else {
              return conditionResults.some(r => r);
            }
          });
          
          // Combine groups based on groupOperator
          if (tagFilter.groupOperator === 'AND') {
            return groupResults.every(r => r);
          } else {
            return groupResults.some(r => r);
          }
        });
        
        console.log('Candidates after tag filter:', filtered.length);
      }
    }
    
    // Then sort
    if (!sortConfig.key) return filtered;
    
    return [...filtered].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle nulls
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';
      
      // Handle booleans
      if (typeof aVal === 'boolean') aVal = aVal ? 1 : 0;
      if (typeof bVal === 'boolean') bVal = bVal ? 1 : 0;
      
      // Handle numbers
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // Handle dates
      if (sortConfig.key === 'sent_at') {
        const aDate = new Date(aVal);
        const bDate = new Date(bVal);
        return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate;
      }
      
      // Handle strings
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      if (sortConfig.direction === 'asc') {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });
  }, [candidates, sortConfig, tagFilter]);

  // ── Lazy rendering ────────────────────────────────────────────────
  // The candidate archive can have thousands of rows. Rendering all of
  // them at once causes React to hitch. We show the first LAZY_BATCH
  // rows initially, then auto-load another batch when the user scrolls
  // near the bottom (IntersectionObserver on a sentinel row inside the
  // table's tbody).
  //
  // displayCount is reset to LAZY_BATCH whenever the sorted list
  // meaningfully changes (new filter, new sort) so the user always
  // sees the top of the freshly-filtered results.
  const LAZY_BATCH = 200;
  const [displayCount, setDisplayCount] = useState(LAZY_BATCH);
  useEffect(() => {
    setDisplayCount(LAZY_BATCH);
  }, [sortedCandidates.length, sortConfig.key, sortConfig.direction]);
  const visibleCandidates = useMemo(
    () => sortedCandidates.slice(0, displayCount),
    [sortedCandidates, displayCount]
  );
  const hasMore = displayCount < sortedCandidates.length;

  // Publish the filtered/sorted candidate list to the assistant so it can
  // "lock on" and analyze what's currently on screen. Sends the full
  // filtered set (sortedCandidates), not just the paginated slice — the
  // snapshot builder bounds the payload size on the assistant side.
  const assistantRows = useMemo(
    () => sortedCandidates.map(c => ({
      candidate_id: c.candidate_id,
      name: c.name,
      email: c.email,
      phone: c.phone,
      location: c.location,
      rating: c.rating,
      status: c.status,
      pipelines: Array.isArray(c.pipeline_names) ? c.pipeline_names.join(', ') : c.pipeline_names,
      client_stages: Array.isArray(c.client_stage_names) ? c.client_stage_names.join(', ') : c.client_stage_names,
      referral_source: c.referral_source,
      expected_base_min: c.expected_base_min,
      expected_base_max: c.expected_base_max,
      tags: Array.isArray(c.custom_tags) ? c.custom_tags.map(t => t.name || t).join(', ') : undefined,
      updated_at: c.updated_at,
    })),
    [sortedCandidates]
  );
  useAssistantContext({
    type: 'candidate_list',
    label: 'Candidates',
    rows: assistantRows,
    columns: ['candidate_id', 'name', 'email', 'phone', 'location', 'rating', 'status', 'pipelines', 'client_stages', 'referral_source', 'expected_base_min', 'expected_base_max', 'tags'],
    totalCount: candidates.length,
    meta: {
      filtered: sortedCandidates.length !== candidates.length,
      sort: sortConfig.key ? { key: sortConfig.key, direction: sortConfig.direction } : null,
    },
  });

  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return;
    const el = sentinelRef.current;
    // CRITICAL: the table renders inside a scrolling <div> (tableRef).
    // If we don't set `root` explicitly, IntersectionObserver watches
    // against the viewport — which never scrolls here — so the observer
    // never fires and lazy loading appears broken. Passing tableRef as
    // root ties visibility to scroll position inside the actual
    // scrolling container.
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setDisplayCount((c) => Math.min(c + LAZY_BATCH, sortedCandidates.length));
        }
      },
      {
        root: tableRef.current || null,
        // Trigger 400px before actually reaching the sentinel so loading
        // feels seamless as the user scrolls.
        rootMargin: '400px',
      }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, sortedCandidates.length]);

  // Saved view handlers (delegate to parent)
  const handleSaveView = () => {
    if (!newViewName.trim() || !onSaveView) return;
    onSaveView(newViewName, { filters, visibleColumns, columnOrder });
    setShowSaveViewModal(false);
    setNewViewName('');
  };

  const handleLoadView = (view) => {
    const viewData = typeof view.filters === 'string' ? JSON.parse(view.filters) : view.filters;
    if (viewData.filters) {
      setFilters(prev => ({ ...prev, ...viewData.filters }));
      if (viewData.visibleColumns) setVisibleColumns(prev => ({ ...prev, ...viewData.visibleColumns }));
      if (viewData.columnOrder) setColumnOrder(viewData.columnOrder);
    } else {
      setFilters(prev => ({ ...prev, ...viewData }));
    }
  };

  const handleDeleteView = (viewId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this saved view?') || !onDeleteView) return;
    onDeleteView(viewId);
  };

  const handleReorderView = (viewId, direction) => {
    if (!onReorderViews) return;
    const currentIndex = savedViews.findIndex(v => v.id === viewId);
    if (currentIndex === -1) return;
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= savedViews.length) return;
    const newViews = [...savedViews];
    const [movedView] = newViews.splice(currentIndex, 1);
    newViews.splice(newIndex, 0, movedView);
    onReorderViews(newViews.map(v => v.id));
  };

  // Quick filter handlers
  const applyQuickFilter = (filterKey, value) => {
    setFilters(prev => ({ ...prev, [filterKey]: prev[filterKey] === value ? '' : value }));
  };

  // Selection handlers
  const handleSelectCandidate = (candidateId, index, event) => {
    const newSelected = new Set(selectedCandidates);
    
    if (event.shiftKey && lastSelectedIndex !== null) {
      // Shift+click: select range (add all in range, don't toggle)
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      for (let i = start; i <= end; i++) {
        newSelected.add(sortedCandidates[i].candidate_id);
      }
    } else {
      // Regular click: toggle single
      if (newSelected.has(candidateId)) {
        newSelected.delete(candidateId);
      } else {
        newSelected.add(candidateId);
      }
      // Update last selected index for shift-click reference
      setLastSelectedIndex(index);
    }
    
    setSelectedCandidates(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedCandidates.size === sortedCandidates.length) {
      setSelectedCandidates(new Set());
    } else {
      setSelectedCandidates(new Set(sortedCandidates.map(c => c.candidate_id)));
    }
  };

  const clearSelection = () => {
    setSelectedCandidates(new Set());
    setLastSelectedIndex(null);
    setBulkAction('');
    setShowPipelineDropdown(false);
    setPipelineSearchQuery('');
    setShowTagDropdown(false);
    setTagSearchQuery('');
  };

  // --- Tag → filter linking (from the Tags tab's linked-candidates badge) ---
  const tagAsCondition = (tag) => ({ tagId: tag.id, tagName: tag.name, tagColor: tag.color, not: false });
  const filterHasConditions = tagFilter.groups?.some(g => g.conditions?.length > 0);
  const filterHasTag = (tagId) => tagFilter.groups?.some(g => g.conditions?.some(c => String(c.tagId) === String(tagId)));

  const applyLinkedTag = (tag, mode) => {
    const cond = tagAsCondition(tag);
    if (mode === 'replace' || !filterHasConditions) {
      setTagFilter({ groups: [{ operator: 'AND', conditions: [cond] }], groupOperator: 'OR' });
    } else if (mode === 'or') {
      const groups = tagFilter.groups.map(g => ({ ...g, conditions: [...g.conditions] }));
      groups.push({ operator: 'AND', conditions: [cond] });
      setTagFilter({ ...tagFilter, groups, groupOperator: 'OR' });
    } else if (mode === 'and') {
      const groups = tagFilter.groups.map(g => ({ ...g, conditions: [...g.conditions] }));
      if (groups.length === 0) groups.push({ operator: 'AND', conditions: [] });
      groups[groups.length - 1].conditions.push(cond);
      setTagFilter({ ...tagFilter, groups, groupOperator: 'OR' });
    }
    setTagsView(false);      // back to All Candidates
    setTagSelection(null);   // drop any tag-manager selection
    setPendingLinkTag(null);
  };

  const handleLinkTag = (tag) => {
    // Already in the filter → just switch to the candidate view, no dupes.
    if (filterHasTag(tag.id)) { setTagsView(false); setTagSelection(null); return; }
    // No filter yet → apply directly. Otherwise ask how to merge.
    if (!filterHasConditions) applyLinkedTag(tag, 'replace');
    else setPendingLinkTag(tag);
  };

  // Bulk add tag to selected candidates
  const handleBulkAddTag = async (tag) => {
    if (selectedCandidates.size === 0 || !onBulkAddTag) return;
    setBulkActionLoading(true);
    try {
      await onBulkAddTag(tag, Array.from(selectedCandidates));
      setShowTagDropdown(false);
      setTagSearchQuery('');
      setBulkAction('');
      clearSelection();
    } catch (err) {
      console.error('Error in bulk tag add:', err);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk add to pipeline/search
  const handleBulkAddToPipeline = async (pipelineId) => {
    if (selectedCandidates.size === 0 || !onBulkAddToPipeline) return;
    setBulkActionLoading(true);
    try {
      await onBulkAddToPipeline(pipelineId, Array.from(selectedCandidates));
      clearSelection();
      setBulkAction('');
    } catch (err) {
      console.error('Error in bulk add:', err);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk delete candidates - show confirmation modal
  const handleBulkDelete = () => {
    if (selectedCandidates.size === 0) return;
    setShowDeleteConfirmModal(true);
    setDeleteConfirmText('');
  };

  // Execute the actual bulk delete after confirmation
  const executeBulkDelete = async () => {
    if (deleteConfirmText !== 'DELETE' || !onBulkDelete) return;
    setShowDeleteConfirmModal(false);
    setBulkActionLoading(true);
    try {
      await onBulkDelete(Array.from(selectedCandidates));
      clearSelection();
    } catch (err) {
      console.error('Error in bulk delete:', err);
    } finally {
      setBulkActionLoading(false);
      setDeleteConfirmText('');
    }
  };

  // Single delete
  const handleDeleteCandidate = async (candidateId, event) => {
    event.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this candidate? This action cannot be undone.')) return;
    if (onDeleteCandidate) {
      await onDeleteCandidate(candidateId);
    }
  };

  const getStatusBadge = (candidate) => {
    const status = candidate.derived_status || candidate.status;
    const colors = {
      placed_and_interviewing: 'bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700',
      placed: 'bg-purple-100 text-purple-700',
      interviewing: 'bg-blue-100 text-blue-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      pending: 'bg-yellow-100 text-yellow-700',
      none: 'bg-gray-100 text-gray-500'
    };
    const labels = {
      placed_and_interviewing: 'Placed + Interviewing',
      placed: 'Placed',
      interviewing: 'Interviewing',
      approved: 'Approved',
      rejected: 'Rejected',
      pending: 'Pending',
      none: '—'
    };
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${colors[status] || colors.none}`}>
        {labels[status] || '—'}
      </span>
    );
  };

  // Format pipeline names array for display
  const formatPipelines = (pipelineNames) => {
    if (!pipelineNames || pipelineNames.length === 0) return '—';
    if (pipelineNames.length === 1) return pipelineNames[0];
    return `${pipelineNames[0]} +${pipelineNames.length - 1}`;
  };

  // Format client stage names array for display
  const formatClientStages = (stageNames) => {
    if (!stageNames || stageNames.length === 0) return '—';
    // Filter out duplicates and join
    const unique = [...new Set(stageNames)];
    if (unique.length === 1) return unique[0];
    return unique.join(', ');
  };

  const formatCurrency = (value) => {
    if (!value) return '—';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
  };

  // Render a cell based on column key
  const renderCell = (colKey, candidate) => {
    const width = getColumnWidth(colKey);
    
    switch (colKey) {
      case 'name':
        const candidateIdForSync = candidate.candidate_id || candidate.id;
        const isSyncing = syncingIds && syncingIds.has && syncingIds.has(candidateIdForSync);
        return (
          <td key={colKey} className="px-3 py-2 whitespace-nowrap" style={{ width }}>
            <div className="flex items-center gap-2">
              {candidate.profile_picture_url ? (
                <img src={candidate.profile_picture_url} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0"
                  onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; e.target.parentElement.querySelector('.initials-fallback').style.display = 'flex'; }} />
              ) : null}
              <div className={`initials-fallback w-7 h-7 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 text-white text-xs font-bold items-center justify-center flex-shrink-0 ${candidate.profile_picture_url ? 'hidden' : 'flex'}`}>
                {candidate.name?.charAt(0)?.toUpperCase()}
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); setPreviewCandidateId(candidate.candidate_id); setIsPreviewOpen(true); }}
                className="font-medium text-gray-900 truncate text-left hover:text-emerald-700 hover:underline"
                style={{ maxWidth: width - 100 }}
                title={candidate.name}
              >
                {candidate.name}
              </button>
              {isSyncing && (
                <span className="flex-shrink-0 flex items-center gap-1 text-[10px] text-[#0A66C2] font-medium" title="Syncing from LinkedIn...">
                  <div className="animate-spin rounded-full h-3 w-3 border-[1.5px] border-[#0A66C2] border-t-transparent"></div>
                </span>
              )}
              {candidate.linkedin && (
                <a
                  href={candidate.linkedin.startsWith('http') ? candidate.linkedin : `https://${candidate.linkedin}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="flex-shrink-0 text-[#0A66C2] hover:text-[#004182] transition-colors"
                  title="View LinkedIn Profile"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                  </svg>
                </a>
              )}
              {rowAfterName && rowAfterName(candidate)}
            </div>
          </td>
        );
      case 'title':
        return (
          <td key={colKey} className="px-3 py-2 whitespace-nowrap text-gray-600 truncate" style={{ width, maxWidth: width }}>
            {candidate.client_current_title || candidate.current_title || '—'}
          </td>
        );
      case 'company':
        return (
          <td key={colKey} className="px-3 py-2 whitespace-nowrap text-gray-600 truncate" style={{ width, maxWidth: width }}>
            {candidate.client_current_company || candidate.current_company || '—'}
          </td>
        );
      case 'tags':
        return (
          <td key={colKey} className="px-3 py-2" style={{ width }}>
            <div className="flex flex-wrap gap-0.5" style={{ maxWidth: width }}>
              {candidate.custom_tags && Object.keys(candidate.custom_tags).length > 0 ? (
                <>
                  {Object.entries(candidate.custom_tags).slice(0, 2).map(([tagId, tagData]) => {
                    const isOldFormat = typeof tagData === 'string';
                    const tagName = isOldFormat ? tagId : (tagData?.name || tagId);
                    const tagColor = isOldFormat ? '#6B7280' : (tagData?.color || '#6B7280');
                    return (
                      <span key={tagId} className="tag-color px-1.5 py-0.5 text-[10px] rounded whitespace-nowrap" style={{ backgroundColor: `${tagColor}20`, color: tagColor }}>
                        {tagName}
                      </span>
                    );
                  })}
                  {Object.keys(candidate.custom_tags).length > 2 && (
                    <span className="text-[10px] text-gray-400">+{Object.keys(candidate.custom_tags).length - 2}</span>
                  )}
                </>
              ) : (
                <span className="text-gray-200">—</span>
              )}
            </div>
          </td>
        );
      case 'location':
        return (
          <td key={colKey} className="px-3 py-2 whitespace-nowrap text-gray-600 truncate" style={{ width, maxWidth: width }}>
            {candidate.display_location || candidate.client_location || candidate.location || '—'}
          </td>
        );
      case 'email':
        return (
          <td key={colKey} className="px-3 py-2 whitespace-nowrap text-sm" style={{ width, maxWidth: width }} onClick={(e) => e.stopPropagation()}>
            {candidate.email ? (
              <div className="flex items-center gap-1 group">
                <span className="text-gray-600 truncate">{candidate.email}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(candidate.email);
                    const btn = e.currentTarget;
                    btn.classList.add('text-green-600');
                    setTimeout(() => btn.classList.remove('text-green-600'), 1000);
                  }}
                  className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-400 hover:text-emerald-600 transition-all"
                  title="Copy email"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            ) : <span className="text-gray-300">—</span>}
          </td>
        );
      case 'phone':
        return (
          <td key={colKey} className="px-3 py-2 whitespace-nowrap text-sm" style={{ width, maxWidth: width }} onClick={(e) => e.stopPropagation()}>
            {candidate.phone ? (
              <div className="flex items-center gap-1 group">
                <span className="text-gray-600 truncate">{candidate.phone}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(candidate.phone);
                    const btn = e.currentTarget;
                    btn.classList.add('text-green-600');
                    setTimeout(() => btn.classList.remove('text-green-600'), 1000);
                  }}
                  className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-400 hover:text-emerald-600 transition-all"
                  title="Copy phone"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            ) : <span className="text-gray-300">—</span>}
          </td>
        );
      case 'pipeline':
        return (
          <td key={colKey} className="px-3 py-2 whitespace-nowrap text-gray-600 truncate" style={{ width, maxWidth: width }} title={candidate.pipeline_names?.join(', ')}>
            {formatPipelines(candidate.pipeline_names)}
          </td>
        );
      case 'stage':
        return (
          <td key={colKey} className="px-3 py-2 whitespace-nowrap text-gray-600 text-xs" style={{ width }} title={candidate.client_stage_names?.join(', ')}>
            {formatClientStages(candidate.client_stage_names)}
          </td>
        );
      case 'placed':
        return (
          <td key={colKey} className="px-3 py-2 whitespace-nowrap text-center" style={{ width }}>
            {candidate.has_placed ? (
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-purple-100 text-purple-700">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </span>
            ) : <span className="text-gray-200">—</span>}
          </td>
        );
      case 'interviewing':
        return (
          <td key={colKey} className="px-3 py-2 whitespace-nowrap text-center" style={{ width }}>
            {candidate.has_interviewing ? (
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-700">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </span>
            ) : <span className="text-gray-200">—</span>}
          </td>
        );
      case 'referral_source':
        return (
          <td key={colKey} className="px-3 py-2 whitespace-nowrap text-gray-600 truncate" style={{ width, maxWidth: width }}>
            {candidate.referral_source || '—'}
          </td>
        );
      case 'current_base':
        return (
          <td key={colKey} className="px-3 py-2 whitespace-nowrap text-gray-600 text-xs" style={{ width }}>
            {formatCurrency(candidate.current_base_salary)}
          </td>
        );
      case 'current_total':
        return (
          <td key={colKey} className="px-3 py-2 whitespace-nowrap text-gray-600 text-xs" style={{ width }}>
            {formatCurrency(candidate.current_total_comp)}
          </td>
        );
      case 'expected_base':
        return (
          <td key={colKey} className="px-3 py-2 whitespace-nowrap text-gray-600 text-xs" style={{ width }}>
            {candidate.expected_base_min || candidate.expected_base_max ? `${formatCurrency(candidate.expected_base_min)} - ${formatCurrency(candidate.expected_base_max)}` : '—'}
          </td>
        );
      case 'remote':
        return (
          <td key={colKey} className="px-3 py-2 whitespace-nowrap text-center text-xs" style={{ width }}>
            {candidate.remote_only ? <span className="text-purple-600 font-medium">Only</span> : candidate.open_to_remote ? <span className="text-green-600">Yes</span> : <span className="text-gray-300">—</span>}
          </td>
        );
      case 'relocation':
        return (
          <td key={colKey} className="px-3 py-2 whitespace-nowrap text-center text-xs" style={{ width }}>
            {candidate.open_to_relocation ? <span className="text-green-600">Yes</span> : <span className="text-gray-300">—</span>}
          </td>
        );
      case 'date_updated':
        return (
          <td key={colKey} className="px-3 py-2 whitespace-nowrap text-gray-400 text-xs" style={{ width }}>
            {formatDate(candidate.updated_at)}
          </td>
        );
      case 'notes_updated':
        return (
          <td key={colKey} className="px-3 py-2 whitespace-nowrap text-gray-400 text-xs" style={{ width }}>
            {formatDate(candidate.last_note_at)}
          </td>
        );
      case 'sent_date':
        return (
          <td key={colKey} className="px-3 py-2 whitespace-nowrap text-gray-400 text-xs" style={{ width }}>
            {formatDate(candidate.sent_at)}
          </td>
        );
      case 'rating':
        return (
          <td key={colKey} className="px-3 py-2 whitespace-nowrap text-xs" style={{ width }}>
            {candidate.rating ? <span className="text-yellow-500">{'★'.repeat(candidate.rating)}</span> : <span className="text-gray-200">—</span>}
          </td>
        );
      default:
        return <td key={colKey} className="px-3 py-2">—</td>;
    }
  };

  if (loading && candidates.length === 0) {
    return (
      <div className={embedded ? "flex items-center justify-center py-12" : "flex items-center justify-center h-[calc(100vh-200px)]"}>
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className={embedded ? "flex flex-col h-full" : "flex-1 flex flex-col overflow-hidden"}>

      {/* Tag Filter Bar — replaced by the bulk action bar when candidates are selected */}
      {showTagFilter && selectedCandidates.size === 0 && !(tagsView && tagSelection) && (
      <TagFilterBar
        availableTags={filterOptions.tags}
        value={tagFilter}
        onChange={setTagFilter}
        onManageTags={!isClient ? () => setTagsView(v => { if (v) setTagSelection(null); return !v; }) : undefined}
        tagsActive={tagsView}
      />
      )}

      {/* Tag bulk action bar — replaces the tabs row while tags are selected */}
      {tagsView && tagSelection && (
        <BulkActionBar
          count={tagSelection.count}
          onClear={tagSelection.onClear}
          actions={[
            { label: 'Export CSV', onClick: tagSelection.onExport },
          ]}
        />
      )}

      {/* Bulk action bar — replaces the Tag Filter Bar when candidates are selected.
          Suppressed when the parent renders the bulk bar in the pageStrip instead. */}
      {!suppressBulkBar && selectedCandidates.size > 0 && (
        <BulkActionBar
          count={selectedCandidates.size}
          onClear={clearSelection}
          busy={bulkActionLoading}
          actions={[
            ...(onBulkSyncLinkedIn ? [{ label: 'Sync LinkedIn', onClick: () => onBulkSyncLinkedIn(Array.from(selectedCandidates)) }] : []),
            { label: 'Export CSV', onClick: () => exportCandidatesToCSV(sortedCandidates.filter(c => selectedCandidates.has(c.candidate_id)), exportLabel) },
            ...(onBulkDelete ? [{ label: 'Delete', onClick: handleBulkDelete }] : []),
          ]}
        >
          {/* Add Tag picker */}
          <div className="relative">
            <button
              onClick={() => { setBulkAction(bulkAction === 'add_tag' ? '' : 'add_tag'); setShowTagDropdown(bulkAction !== 'add_tag'); setShowPipelineDropdown(false); }}
              disabled={bulkActionLoading}
              className="text-xs px-2.5 py-1 rounded transition-colors text-white/90 hover:bg-white/15 flex items-center gap-1"
            >
              Add Tag
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>
            {bulkAction === 'add_tag' && showTagDropdown && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => { setShowTagDropdown(false); setBulkAction(''); }} />
                <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                <div className="p-2 border-b border-gray-100">
                  <input
                    type="text"
                    value={tagSearchQuery}
                    onChange={(e) => setTagSearchQuery(e.target.value)}
                    placeholder="Search..."
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800"
                    autoFocus
                  />
                </div>
                <div className="max-h-48 overflow-y-auto py-1">
                  {(filterOptions.tags || [])
                    .filter(t => t.name.toLowerCase().includes(tagSearchQuery.toLowerCase()))
                    .map(tag => (
                      <button
                        key={tag.id}
                        onClick={() => handleBulkAddTag(tag)}
                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-emerald-50 flex items-center gap-2"
                      >
                        <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color || '#3B82F6' }} />
                        {tag.name}
                      </button>
                    ))}
                  {(filterOptions.tags || []).filter(t => t.name.toLowerCase().includes(tagSearchQuery.toLowerCase())).length === 0 && (
                    <div className="px-3 py-2 text-sm text-gray-500">{tagSearchQuery ? 'No matching tags' : 'No tags available'}</div>
                  )}
                </div>
              </div>
              </>
            )}
          </div>

          {/* Add to Search picker */}
          <div className="relative">
            <button
              onClick={() => {
                if (contextPipelineId) { handleBulkAddToPipeline(contextPipelineId); return; }
                setBulkAction(bulkAction === 'add_to_pipeline' ? '' : 'add_to_pipeline'); setShowPipelineDropdown(bulkAction !== 'add_to_pipeline'); setShowTagDropdown(false);
              }}
              disabled={bulkActionLoading}
              className="text-xs px-2.5 py-1 rounded transition-colors text-white/90 hover:bg-white/15 flex items-center gap-1"
            >
              {contextPipelineId ? 'Move to Search' : 'Add to Search'}
              {!contextPipelineId && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>}
            </button>
            {bulkAction === 'add_to_pipeline' && showPipelineDropdown && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => { setShowPipelineDropdown(false); setBulkAction(''); }} />
                <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                <div className="p-2 border-b border-gray-100">
                  <input
                    type="text"
                    value={pipelineSearchQuery}
                    onChange={(e) => setPipelineSearchQuery(e.target.value)}
                    placeholder="Search..."
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800"
                    autoFocus
                  />
                </div>
                <div className="max-h-48 overflow-y-auto py-1">
                  {pipelines
                    .filter(p => p.status === 'active')
                    .filter(p => (p.name || '').toLowerCase().includes(pipelineSearchQuery.toLowerCase()) || (p.firm_name || '').toLowerCase().includes(pipelineSearchQuery.toLowerCase()))
                    .map(pipeline => (
                      <button
                        key={pipeline.id}
                        onClick={() => {
                          handleBulkAddToPipeline(pipeline.id);
                          setShowPipelineDropdown(false);
                          setBulkAction('');
                          setPipelineSearchQuery('');
                        }}
                        className="w-full px-3 py-2 text-left hover:bg-emerald-50 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 truncate">{pipeline.name}</div>
                          <div className="text-xs text-gray-500 truncate">{pipeline.firm_name || 'Unknown'} · SR-{String(pipeline.id).padStart(4, '0')}</div>
                        </div>
                      </button>
                    ))}
                  {pipelines.filter(p => p.status === 'active').filter(p => (p.name || '').toLowerCase().includes(pipelineSearchQuery.toLowerCase()) || (p.firm_name || '').toLowerCase().includes(pipelineSearchQuery.toLowerCase())).length === 0 && (
                    <div className="px-3 py-2 text-sm text-gray-500">No pipelines found</div>
                  )}
                </div>
              </div>
              </>
            )}
          </div>

          <div className="h-4 w-px bg-white/25 mx-1" />
        </BulkActionBar>
      )}

      {/* Recruiter tag manager — replaces the candidate content when Tags is active */}
      {tagsView && !isClient && (
        <div className="flex-1 min-h-0 overflow-hidden">
          <RecruiterTagsPage embedded onSelectionChange={setTagSelection} onLinkTag={handleLinkTag} />
        </div>
      )}

      {/* Content row — table area + Views sidebar (starts below the tag bar) */}
      {!tagsView && (
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Quick Filter Sidebar */}
        {showQuickPanel && showQuickFilters && (
          <div className="w-52 flex-shrink-0 bg-white border-r overflow-y-auto">
            <div className="px-3 py-2.5 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Filters</span>
                <div className="flex items-center gap-2">
                  {quickFilterCount > 0 && (
                    <button
                      onClick={() => setFilters(prev => ({
                        ...prev,
                        location: '', referral_source: '', status: '', interview_status: '',
                        open_to_remote: '', remote_only: '', open_to_relocation: '',
                      }))}
                      className="text-[10px] text-gray-400 hover:text-red-500"
                    >
                      Clear all
                    </button>
                  )}
                  <button
                    onClick={() => setShowQuickPanel(false)}
                    className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                    title="Close"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              </div>

              <div>
                <div className="text-[10px] font-medium text-gray-500 mb-1">Location</div>
                <FilterSelect
                  label="Location" value={filters.location} defaultValue="" minWidth={180}
                  onChange={(v) => setFilters({...filters, location: v})}
                  options={[{ value: '', label: 'All Locations' }, ...(filterOptions.locations || []).map(loc => ({ value: loc, label: loc }))]}
                />
              </div>

              <div>
                <div className="text-[10px] font-medium text-gray-500 mb-1">Source</div>
                <FilterSelect
                  label="Source" value={filters.referral_source} defaultValue=""
                  onChange={(v) => setFilters({...filters, referral_source: v})} minWidth={180}
                  options={[{ value: '', label: 'All Sources' }, ...(filterOptions.referral_sources || []).map(src => ({ value: src, label: src }))]}
                />
              </div>

              <div>
                <div className="text-[10px] font-medium text-gray-500 mb-1">Rating</div>
                <FilterSelect
                  label="Rating" value={filters.status} defaultValue="" minWidth={180}
                  onChange={(v) => setFilters({...filters, status: v})}
                  options={[{ value: '', label: 'Any' }, { value: 'approved', label: 'Approved' }, { value: 'pending', label: 'Pending' }, { value: 'rejected', label: 'Rejected' }]}
                />
              </div>

              <div>
                <div className="text-[10px] font-medium text-gray-500 mb-1">Interview</div>
                <FilterSelect
                  label="Interview" value={filters.interview_status} defaultValue="" minWidth={180}
                  onChange={(v) => setFilters({...filters, interview_status: v})}
                  options={[{ value: '', label: 'Any' }, { value: 'interviewing', label: 'Interviewing' }, { value: 'placed', label: 'Placed' }]}
                />
              </div>

              <div>
                <div className="text-[10px] font-medium text-gray-500 mb-1">Remote</div>
                <FilterSelect
                  label="Remote" value={filters.open_to_remote} defaultValue="" minWidth={180}
                  onChange={(v) => setFilters({...filters, open_to_remote: v})}
                  options={[{ value: '', label: 'Any' }, { value: 'true', label: 'Open to Remote' }, { value: 'false', label: 'No Remote' }]}
                />
              </div>

              <div>
                <div className="text-[10px] font-medium text-gray-500 mb-1">Remote Only</div>
                <FilterSelect
                  label="Remote Only" value={filters.remote_only} defaultValue="" minWidth={180}
                  onChange={(v) => setFilters({...filters, remote_only: v})}
                  options={[{ value: '', label: 'Any' }, { value: 'true', label: 'Remote Only' }, { value: 'false', label: 'Not Remote Only' }]}
                />
              </div>

              <div>
                <div className="text-[10px] font-medium text-gray-500 mb-1">Relocate</div>
                <FilterSelect
                  label="Relocate" value={filters.open_to_relocation} defaultValue="" minWidth={180}
                  onChange={(v) => setFilters({...filters, open_to_relocation: v})}
                  options={[{ value: '', label: 'Any' }, { value: 'true', label: 'Will Relocate' }, { value: 'false', label: "Won't Relocate" }]}
                />
              </div>

              {/* Compensation — Target Total + Obligations always visible */}
              <div className="pt-2 border-t border-gray-100 space-y-2">
                <div>
                  <div className="text-[10px] font-medium text-gray-500 mb-1">Target Total $</div>
                  <input
                    type="number"
                    defaultValue={filters.min_expected_total}
                    onBlur={(e) => { if (e.target.value !== (filters.min_expected_total || '')) setFilters({...filters, min_expected_total: e.target.value}); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                    placeholder="Amount"
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <div className="text-[10px] font-medium text-gray-500 mb-1">Obligations ≤ days</div>
                  <input
                    key={filters.max_obligation_days === '' ? 'obl-empty' : 'obl-set'}
                    type="number" min="0"
                    defaultValue={filters.max_obligation_days}
                    onBlur={(e) => { if (e.target.value !== (filters.max_obligation_days || '')) setFilters({...filters, max_obligation_days: e.target.value}); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                    placeholder="days"
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
              </div>

              {/* More — accordion for advanced comp ranges */}
              <div className="pt-2 border-t border-gray-100">
                <button
                  onClick={() => setShowMoreFilters(!showMoreFilters)}
                  className="w-full flex items-center justify-between text-[10px] font-semibold text-gray-500 uppercase tracking-wide hover:text-gray-700"
                >
                  More
                  <svg className={`w-3 h-3 transition-transform ${showMoreFilters ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" /></svg>
                </button>
                {showMoreFilters && (
                  <div className="mt-2 space-y-2">
                    <div>
                      <div className="text-[10px] font-medium text-gray-500 mb-1">Target Base $</div>
                      <input
                        type="number"
                        defaultValue={filters.min_expected_base}
                        onBlur={(e) => { if (e.target.value !== (filters.min_expected_base || '')) setFilters({...filters, min_expected_base: e.target.value}); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                        placeholder="Amount"
                        className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <div className="text-[10px] font-medium text-gray-500 mb-1">Curr. Base $</div>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          defaultValue={filters.min_current_base}
                          onBlur={(e) => { if (e.target.value !== (filters.min_current_base || '')) setFilters({...filters, min_current_base: e.target.value}); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                          placeholder="Min"
                          className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        />
                        <span className="text-[10px] text-gray-400">—</span>
                        <input
                          type="number"
                          defaultValue={filters.max_current_base}
                          onBlur={(e) => { if (e.target.value !== (filters.max_current_base || '')) setFilters({...filters, max_current_base: e.target.value}); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                          placeholder="Max"
                          className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] font-medium text-gray-500 mb-1">Curr. Total $</div>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          defaultValue={filters.min_current_total}
                          onBlur={(e) => { if (e.target.value !== (filters.min_current_total || '')) setFilters({...filters, min_current_total: e.target.value}); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                          placeholder="Min"
                          className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        />
                        <span className="text-[10px] text-gray-400">—</span>
                        <input
                          type="number"
                          defaultValue={filters.max_current_total}
                          onBlur={(e) => { if (e.target.value !== (filters.max_current_total || '')) setFilters({...filters, max_current_total: e.target.value}); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                          placeholder="Max"
                          className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">

      {/* Unified Toolbar */}
      <div className="bg-[color:var(--list-page-bg)] px-3 py-1.5 flex-shrink-0">
        <div className="flex items-center gap-0">
          {/* Left: filter area */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
          {/* Quick Filters toggle */}
          {showQuickFilters && (
            <>
              <button
                onClick={() => setShowQuickPanel(!showQuickPanel)}
                className={`h-[30px] px-2.5 text-sm border rounded flex items-center gap-1 transition-colors ${
                  showQuickPanel || quickFilterCount > 0
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                    : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
                Filters
                {quickFilterCount > 0 && (
                  <span className="ml-0.5 px-1 py-0.5 text-[10px] bg-emerald-600 text-white rounded-full leading-none min-w-[16px] text-center">
                    {quickFilterCount}
                  </span>
                )}
              </button>
            </>
          )}
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters({...filters, search: e.target.value})}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              placeholder="Search..."
              className="h-[30px] w-48 pl-7 pr-2 text-sm border border-gray-300 rounded focus:outline-none transition-shadow"
              style={searchFocused ? { boxShadow: '0 0 0 1px rgba(16, 185, 129, 0.15), 0 0 14px 2px rgba(16, 185, 129, 0.22), 0 0 28px 3px rgba(16, 185, 129, 0.10)', borderColor: 'rgba(16, 185, 129, 0.4)' } : undefined}
            />
            <svg className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {/* Search / Pipeline filter */}
          {showPipelineFilter && (
            <FilterSelect
              label="Search"
              value={filters.pipeline_id}
              defaultValue=""
              onChange={(v) => setFilters({...filters, pipeline_id: v})}
              minWidth={140}
              options={[{ value: '', label: 'All Searches' }, ...pipelines.filter(p => p.status === 'active').map(p => ({ value: p.id, label: p.name }))]}
            />
          )}

          {/* Toggle filters — US Only / Contacted / Clean */}
          <button onClick={() => applyQuickFilter('us_only', 'true')}
            className={`h-[30px] px-2.5 text-sm border rounded transition-colors ${
              filters.us_only === 'true'
                ? 'bg-emerald-50 border-emerald-300 text-emerald-700 font-medium'
                : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}>
            US Only
          </button>
          <button onClick={() => applyQuickFilter('spoken_to', 'true')}
            className={`h-[30px] px-2.5 text-sm border rounded transition-colors ${
              filters.spoken_to === 'true'
                ? 'bg-emerald-50 border-emerald-300 text-emerald-700 font-medium'
                : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}>
            Contacted
          </button>
          <button onClick={() => applyQuickFilter('is_clean', 'true')}
            className={`h-[30px] px-2.5 text-sm border rounded transition-colors ${
              filters.is_clean === 'true'
                ? 'bg-emerald-50 border-emerald-300 text-emerald-700 font-medium'
                : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}>
            Clean
          </button>

          {/* Extra toolbar content from parent */}
          {toolbarExtra}
            </div>
          </div>

          {/* Right: pinned controls */}
          <div className="flex items-center gap-2 flex-shrink-0 ml-2">

          {/* Loading Indicator */}
          {loading && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-emerald-600"></div>
              Loading...
            </div>
          )}

          {/* Clear Filters */}
          {activeFilterCount > 0 && (
            <button
              onClick={clearFilters}
              className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700"
            >
              Clear ({activeFilterCount})
            </button>
          )}

          {/* Column Settings — visibility eye + chevron */}
          <div className="relative">
            <button
              onClick={() => setShowColumnSettings(!showColumnSettings)}
              title="Show/hide columns"
              className="h-[30px] px-1.5 border border-gray-300 rounded text-gray-500 hover:bg-gray-50 flex items-center gap-0.5"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
              <svg className={`w-3 h-3 transition-transform ${showColumnSettings ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" /></svg>
            </button>
            {showColumnSettings && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowColumnSettings(false)} />
                <div className="absolute right-0 mt-1 w-52 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1 max-h-80 overflow-y-auto">
                  <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Columns</div>
                  {allColumns.map(col => (
                    <button
                      key={col.key}
                      onClick={() => !col.alwaysVisible && setVisibleColumns({...visibleColumns, [col.key]: !visibleColumns[col.key]})}
                      disabled={col.alwaysVisible}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 transition-colors ${col.alwaysVisible ? 'opacity-50 cursor-not-allowed' : 'hover:bg-emerald-50'}`}
                    >
                      <input
                        type="checkbox"
                        readOnly
                        checked={visibleColumns[col.key]}
                        className="w-4 h-4 rounded border-gray-300 accent-emerald-600 pointer-events-none"
                      />
                      {col.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Views button — opens the Saved Views sidebar */}
          {showSavedViews && (
            <button
              onClick={() => { setShowViewsSidebar(true); }}
              title="Saved views"
              className="h-[30px] px-2.5 text-xs text-gray-700 border border-gray-300 rounded flex items-center gap-1.5 hover:bg-gray-50"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
              Views
            </button>
          )}

          {/* Add Candidate button */}
          {showAddButton && onAddButtonClick && (
            <button
              onClick={onAddButtonClick}
              className="h-[30px] px-3 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 flex items-center gap-1.5 flex-shrink-0"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add
            </button>
          )}

          </div>{/* close right pinned */}
        </div>{/* close outer flex */}

      </div>

      {/* Results Area — table */}
      <div className="flex-1 overflow-hidden flex">

        {/* Results Grid */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {candidates.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p className="text-gray-500 text-sm mb-1">No candidates found</p>
                <p className="text-xs text-gray-400 mb-3">Try adjusting your filters</p>
                <button
                  onClick={onAddButtonClick}
                  className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700"
                >
                  Add Candidate
                </button>
              </div>
            </div>
          ) : (
            <div className="flex-1 min-h-0 mx-3 mb-1 bg-[color:var(--list-block-bg)] border rounded-lg shadow-sm overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 overflow-x-auto flex flex-col" ref={tableRef}>
              <table className="text-sm flex flex-col min-h-0 flex-1" style={{ minWidth: 'max-content' }}>
                <thead className="flex-shrink-0" style={{ display: 'block', backgroundColor: 'var(--list-header-bg)', color: 'var(--list-header-text)' }}>
                  <tr style={{ display: 'table', width: '100%' }}>
                    {/* Select All Checkbox */}
                    <th className="px-2 py-2 text-center" style={{ width: 40, backgroundColor: 'var(--list-header-bg)', borderBottom: '1px solid var(--list-header-border)' }}>
                      <input
                        type="checkbox"
                        checked={sortedCandidates.length > 0 && selectedCandidates.size === sortedCandidates.length}
                        onChange={handleSelectAll}
                        className="w-4 h-4 rounded border-gray-300 accent-emerald-600 focus:ring-emerald-500"
                      />
                    </th>
                    {allColumns.filter(col => visibleColumns[col.key]).map(col => (
                      <th 
                        key={col.key} 
                        draggable={!col.alwaysVisible}
                        onDragStart={(e) => handleColumnDragStart(e, col.key)}
                        onDragEnd={handleColumnDragEnd}
                        onDragOver={handleColumnDragOver}
                        onDrop={(e) => handleColumnDrop(e, col.key)}
                        className={`px-3 py-2 text-left text-[10px] font-medium uppercase tracking-wider relative ${col.sortKey ? 'cursor-pointer hover:bg-emerald-100/50 select-none' : ''} ${draggedColumn === col.key ? 'opacity-50' : ''} ${!col.alwaysVisible ? 'cursor-grab active:cursor-grabbing' : ''}`}
                        style={{ width: getColumnWidth(col.key), minWidth: col.minWidth, backgroundColor: 'var(--list-header-bg)', color: 'var(--list-header-text)', borderBottom: '1px solid var(--list-header-border)' }}
                        onClick={() => col.sortKey && handleSort(col.sortKey)}
                      >
                        <div className="flex items-center gap-1 pr-2">
                          {/* Drag handle indicator */}
                          {!col.alwaysVisible && (
                            <svg className="w-3 h-3 text-gray-300 flex-shrink-0 mr-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z"/>
                            </svg>
                          )}
                          <span className="truncate">{col.label}</span>
                          {col.sortKey && (
                            <span className="text-gray-400 flex-shrink-0">
                              {sortConfig.key === col.sortKey ? (
                                sortConfig.direction === 'asc' ? '↑' : '↓'
                              ) : (
                                <span className="opacity-30">↕</span>
                              )}
                            </span>
                          )}
                        </div>
                        {/* Resize handle */}
                        <div
                          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-emerald-400 group"
                          onMouseDown={(e) => handleResizeStart(e, col.key)}
                          onClick={(e) => e.stopPropagation()}
                          draggable={false}
                        >
                          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-0.5 h-4 bg-gray-300 group-hover:bg-emerald-500" />
                        </div>
                      </th>
                    ))}
                    {rowActions && (
                      <th className="px-2 py-2 text-right text-[10px] font-medium uppercase tracking-wider" style={{ width: 100, backgroundColor: 'var(--list-header-bg)', color: 'var(--list-header-text)', borderBottom: '1px solid var(--list-header-border)' }}>
                        Actions
                      </th>
                    )}
                    <th className="w-auto" style={{ backgroundColor: 'var(--list-header-bg)', borderBottom: '1px solid var(--list-header-border)' }}></th>{/* filler to stretch table */}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 flex-1 min-h-0 overflow-y-auto" style={{ display: 'block', scrollbarGutter: 'stable' }}>
                  {visibleCandidates.map((candidate, index) => (
                    <tr 
                      key={`${candidate.candidate_id}-${index}`} 
                      data-context-type="candidate"
                      data-context-id={candidate.candidate_id}
                      style={{ display: 'table', width: '100%' }}
                      className={`transition-colors ${selectedCandidates.has(candidate.candidate_id) ? 'bg-emerald-100/60' : (previewCandidateId === candidate.candidate_id ? 'bg-emerald-100' : 'bg-white/60 hover:bg-emerald-50/50')}`}
                    >
                      {/* Row Checkbox */}
                      <td className="px-2 py-2 text-center" style={{ width: 40 }} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedCandidates.has(candidate.candidate_id)}
                          onChange={(e) => {
                            // Create a synthetic event object with shiftKey
                            handleSelectCandidate(candidate.candidate_id, index, { shiftKey: e.nativeEvent.shiftKey });
                          }}
                          className="w-4 h-4 rounded border-gray-300 accent-emerald-600 focus:ring-emerald-500 cursor-pointer"
                        />
                      </td>
                      {/* Dynamic columns based on order */}
                      {allColumns.filter(col => visibleColumns[col.key]).map(col => renderCell(col.key, candidate))}
                      {/* Custom row actions from parent */}
                      {rowActions && (
                        <td className="px-2 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          {rowActions(candidate)}
                        </td>
                      )}
                      <td></td>{/* filler */}
                    </tr>
                  ))}
                  {hasMore && (
                    <tr ref={sentinelRef} style={{ display: 'table', width: '100%' }}>
                      <td colSpan={99} className="px-2 py-4 text-center text-xs text-gray-400">
                        Loading more candidates… ({visibleCandidates.length.toLocaleString()} of {sortedCandidates.length.toLocaleString()})
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            </div>
          )}
          {/* Count footer — bottom-right below the list */}
          {!embedded && (
            <div className="flex-shrink-0 px-4 py-1.5 flex justify-end">
              <span className="text-[11px] text-gray-400 tabular-nums whitespace-nowrap">
                <span className={selectedCandidates.size > 0 ? 'font-semibold text-emerald-600' : ''}>{selectedCandidates.size} selected</span>
                <span className="mx-1 text-gray-300">/</span>
                <span className="font-medium text-gray-500">{candidates.length}</span>
              </span>
            </div>
          )}
        </div>
      </div>{/* close Results Area flex */}
      </div>{/* close content column */}

      {/* Views Sidebar — right sidebar, pushes content left (flex sibling) */}
      {showViewsSidebar && (
        <div className="flex-shrink-0 w-[340px] bg-white border-l border-gray-200 flex flex-col animate-[slidein_180ms_ease-out] overflow-hidden">
          <style>{`@keyframes slidein { from { width: 0; opacity: 0;} to { width: 340px; opacity: 1;} }`}</style>
          <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Saved Views</h3>
            <button onClick={() => setShowViewsSidebar(false)} className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          <div className="flex-shrink-0 px-4 py-3 border-b border-gray-100">
            <button
              onClick={() => { setShowSaveViewModal(true); setShowViewsSidebar(false); }}
              className="w-full px-3 py-2 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 flex items-center justify-center gap-1.5"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              Save Current View
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {savedViews.length > 0 ? (
              savedViews.map((view, index) => (
                <div key={view.id} className="flex items-center gap-1 px-2 py-2 hover:bg-gray-50 rounded group/item">
                  <div className="flex flex-col">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleReorderView(view.id, 'up'); }}
                      disabled={index === 0}
                      className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move up"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleReorderView(view.id, 'down'); }}
                      disabled={index === savedViews.length - 1}
                      className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move down"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </button>
                  </div>
                  <button
                    className="flex-1 truncate text-sm text-gray-700 text-left hover:text-emerald-700"
                    onClick={() => { handleLoadView(view); setShowViewsSidebar(false); }}
                  >
                    {view.name}
                  </button>
                  <button
                    onClick={(e) => handleDeleteView(view.id, e)}
                    className="p-1 text-gray-400 hover:text-red-600 opacity-0 group-hover/item:opacity-100"
                    title="Delete view"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              ))
            ) : (
              <div className="px-3 py-8 text-center text-sm text-gray-400">No saved views yet</div>
            )}
          </div>
        </div>
      )}
      </div>
      )}

      {/* Tag link — merge choice when a filter already exists */}
      {pendingLinkTag && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setPendingLinkTag(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b">
              <h3 className="font-semibold text-gray-900 text-sm">Add tag to your filter</h3>
              <p className="text-xs text-gray-500 mt-1">
                You already have a tag filter applied. How should
                {' '}
                <span className="font-medium" style={{ color: pendingLinkTag.color || '#374151' }}>{pendingLinkTag.name}</span>
                {' '}be added?
              </p>
            </div>
            <div className="p-3 space-y-1.5">
              <button
                onClick={() => applyLinkedTag(pendingLinkTag, 'or')}
                className="w-full text-left px-3 py-2 rounded hover:bg-emerald-50 border border-gray-200"
              >
                <span className="text-sm font-medium text-gray-800">Add with OR</span>
                <span className="block text-[11px] text-gray-500">Candidates matching your filter <em>or</em> this tag</span>
              </button>
              <button
                onClick={() => applyLinkedTag(pendingLinkTag, 'and')}
                className="w-full text-left px-3 py-2 rounded hover:bg-emerald-50 border border-gray-200"
              >
                <span className="text-sm font-medium text-gray-800">Add with AND</span>
                <span className="block text-[11px] text-gray-500">Narrow to candidates that also have this tag</span>
              </button>
              <button
                onClick={() => applyLinkedTag(pendingLinkTag, 'replace')}
                className="w-full text-left px-3 py-2 rounded hover:bg-emerald-50 border border-gray-200"
              >
                <span className="text-sm font-medium text-gray-800">Replace filter</span>
                <span className="block text-[11px] text-gray-500">Clear the current filter and use only this tag</span>
              </button>
            </div>
            <div className="px-3 pb-3">
              <button onClick={() => setPendingLinkTag(null)} className="w-full px-3 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Save View Modal */}
      {showSaveViewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-80 p-5">
            <h3 className="text-base font-semibold mb-3">Save Current View</h3>
            <input
              type="text"
              value={newViewName}
              onChange={(e) => setNewViewName(e.target.value)}
              placeholder="View name..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 mb-3"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleSaveView()}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowSaveViewModal(false); setNewViewName(''); }}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveView}
                disabled={!newViewName.trim()}
                className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-96 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete {selectedCandidates.size} Candidates</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              You are about to permanently delete <span className="font-semibold text-red-600">{selectedCandidates.size}</span> candidate(s) 
              and all associated data including pipeline cards, ratings, and notes.
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="font-mono bg-gray-100 px-1 rounded text-red-600">DELETE</span> to confirm
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Type DELETE here"
                autoFocus
              />
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirmModal(false);
                  setDeleteConfirmText('');
                  setBulkAction('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={executeBulkDelete}
                disabled={deleteConfirmText !== 'DELETE'}
                className={`flex-1 px-4 py-2 rounded-lg font-medium ${
                  deleteConfirmText === 'DELETE'
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Candidate Preview Panel */}
      <CandidatePreviewPanel
        candidateId={previewCandidateId}
        isOpen={isPreviewOpen}
        onClose={() => {
          setIsPreviewOpen(false);
          setPreviewCandidateId(null);
        }}
        onPrevCandidate={() => {
          // Find current position in the sorted list and move up one.
          // Guarded on !== -1 so a candidate that fell out of the list
          // (e.g., filter changed while preview was open) doesn't crash.
          const idx = sortedCandidates.findIndex(c => c.candidate_id === previewCandidateId);
          if (idx > 0) setPreviewCandidateId(sortedCandidates[idx - 1].candidate_id);
        }}
        onNextCandidate={() => {
          const idx = sortedCandidates.findIndex(c => c.candidate_id === previewCandidateId);
          if (idx !== -1 && idx < sortedCandidates.length - 1) {
            setPreviewCandidateId(sortedCandidates[idx + 1].candidate_id);
          }
        }}
        // Booleans read the same list — safe to compute inline.
        hasPrev={(() => {
          const idx = sortedCandidates.findIndex(c => c.candidate_id === previewCandidateId);
          return idx > 0;
        })()}
        hasNext={(() => {
          const idx = sortedCandidates.findIndex(c => c.candidate_id === previewCandidateId);
          return idx !== -1 && idx < sortedCandidates.length - 1;
        })()}
      />
    </div>
  );
};

export default CandidateTable;
